/**
 * LienFirmPreferencesBanner
 *
 * Displays firm-level lien management workflow preferences
 * when editing a lien notice package.
 */

import {
  BuildingOffice2Icon,
  DocumentTextIcon,
  InformationCircleIcon,
  UserGroupIcon,
} from '@heroicons/react/20/solid';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type {
  LienManagementWorkflowConfig,
  LienNoticeDocument,
  OpenFileTaskInsuranceType,
  OpenFileTaskRecipient,
} from '@/services/api/generated/firmsService';

// Human-readable labels for config values
const INSURANCE_TYPE_LABELS: Record<OpenFileTaskInsuranceType, string> = {
  MEDICARE: 'Medicare',
  MEDICAID: 'Medicaid',
  PRIVATE_INSURANCE: 'Private Insurance',
};

const DOCUMENT_LABELS: Record<LienNoticeDocument, string> = {
  HIPAA_AUTH: 'HIPAA Authorization',
  HEALTH_INSURANCE_CARD: 'Health Insurance Card',
  DOR: 'Direction of Representation',
};

const RECIPIENT_LABELS: Record<OpenFileTaskRecipient, string> = {
  HEALTH_INSURANCE_ONLY: 'Health Insurance Only',
  HEALTH_INSURANCE_AND_SUBROGATION: 'Health Insurance & Subrogation',
};

interface LienFirmPreferencesBannerProps {
  config: LienManagementWorkflowConfig;
  notes?: string | null;
}

export const LienFirmPreferencesBanner: React.FC<
  LienFirmPreferencesBannerProps
> = ({ config, notes }) => {
  const {
    open_file_insurance_types = [],
    lien_notice_documents = [],
    other_documents_to_include,
    open_file_recipient,
    firm_state_instructions,
  } = config;

  // Check if there's any meaningful config to display
  const hasOpenFileTypes = open_file_insurance_types.length > 0;
  const hasDocuments =
    lien_notice_documents.length > 0 || !!other_documents_to_include;
  const hasRecipient = !!open_file_recipient;
  const hasInstructions = !!firm_state_instructions || !!notes;

  const hasAnyConfig =
    hasOpenFileTypes || hasDocuments || hasRecipient || hasInstructions;

  if (!hasAnyConfig) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50/50 rounded-2xl border border-blue-100/80 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-blue-100/60 flex items-center gap-2">
        <BuildingOffice2Icon className="size-4 text-blue-600" />
        <span className="text-xs font-semibold text-blue-900">
          Firm Preferences
        </span>
        <Tooltip>
          <TooltipTrigger asChild>
            <InformationCircleIcon className="size-3.5 text-blue-400 cursor-help" />
          </TooltipTrigger>
          <TooltipContent side="right" className="max-w-xs text-xs">
            These preferences are configured at the firm level for lien notice
            workflows. They help guide consistent handling across all matters.
          </TooltipContent>
        </Tooltip>
      </div>

      {/* Content */}
      <div className="px-4 py-3 space-y-3">
        {/* Open File Insurance Types */}
        {hasOpenFileTypes && (
          <PreferenceRow
            icon={UserGroupIcon}
            label="Open File For"
            value={open_file_insurance_types
              .map((t) => INSURANCE_TYPE_LABELS[t])
              .join(', ')}
          />
        )}

        {/* Lien Notice Documents */}
        {hasDocuments && (
          <PreferenceRow
            icon={DocumentTextIcon}
            label="Include Documents"
            value={[
              ...lien_notice_documents.map((d) => DOCUMENT_LABELS[d]),
              ...(other_documents_to_include
                ? [other_documents_to_include]
                : []),
            ].join(', ')}
          />
        )}

        {/* Recipient */}
        {hasRecipient && (
          <PreferenceRow
            icon={UserGroupIcon}
            label="Send Open File To"
            value={RECIPIENT_LABELS[open_file_recipient!]}
          />
        )}

        {/* Instructions/Notes */}
        {hasInstructions && (
          <div className="pt-2 border-t border-blue-100/60">
            {firm_state_instructions && (
              <p className="text-xs text-blue-800/80 leading-relaxed">
                <span className="font-medium text-blue-900">Instructions:</span>{' '}
                {firm_state_instructions}
              </p>
            )}
            {notes && (
              <p className="text-xs text-blue-800/70 leading-relaxed mt-1 italic">
                {notes}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

interface PreferenceRowProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}

const PreferenceRow: React.FC<PreferenceRowProps> = ({
  icon: Icon,
  label,
  value,
}) => (
  <div className="flex items-start gap-2.5">
    <Icon className="size-3.5 text-blue-500 mt-0.5 shrink-0" />
    <div className="min-w-0 flex-1">
      <span className="text-[11px] font-medium text-blue-700 block">
        {label}
      </span>
      <span className="text-xs text-blue-900/90">{value}</span>
    </div>
  </div>
);
